import { useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import useFetch from "../../clients/features/get_withcredentials";
import {
  TbSunrise, TbHome, TbUsers,
  TbCalendarCheck, TbAlertTriangle,
  TbStar, TbBed,
  TbChevronRight, TbRefresh,
  TbCurrencyEuro, TbActivity,
  TbDoorEnter, TbDoorExit, TbMoonStars,
  TbWorld, TbBuildingStore,
  TbPercentage
} from "react-icons/tb";

// ── Utils ─────────────────────────────────────────────────────────
const normalize = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const today = () => normalize(new Date());
const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
const fmtShort = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const fmtTime = () => new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const KMF_PER_EUR = 491.96775;
const toKMF = (eur) => Math.round(eur * KMF_PER_EUR).toLocaleString("fr-FR");

// ── Composants UI ─────────────────────────────────────────────────
function Pill({ children, color = "gray" }) {
  const colors = {
    green:  "bg-emerald-50 text-emerald-700 ring-emerald-200",
    orange: "bg-orange-50 text-orange-700 ring-orange-200",
    blue:   "bg-blue-50 text-blue-700 ring-blue-200",
    gray:   "bg-gray-100 text-gray-600 ring-gray-200",
    purple: "bg-violet-50 text-violet-700 ring-violet-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ${colors[color]}`}>
      {children}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, subColor, color = "gray", badge }) {
  const colors = {
    green:  { bg: "bg-emerald-50",  icon: "text-emerald-600", val: "text-emerald-700" },
    blue:   { bg: "bg-blue-50",     icon: "text-blue-600",    val: "text-blue-700" },
    orange: { bg: "bg-orange-50",   icon: "text-orange-500",  val: "text-orange-600" },
    purple: { bg: "bg-violet-50",   icon: "text-violet-600",  val: "text-violet-700" },
    gray:   { bg: "bg-gray-50",     icon: "text-gray-500",    val: "text-gray-800" },
  };
  const c = colors[color];
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}>
          <Icon className={`${c.icon} text-lg`} />
        </div>
        {badge && badge}
      </div>
      <div>
        <p className="text-xs text-gray-400 mb-0.5">{label}</p>
        <p className={`text-2xl font-bold tracking-tight ${c.val}`}>{value}</p>
        {sub && <p className={`text-xs mt-0.5 ${subColor || "text-gray-400"}`}>{sub}</p>}
      </div>
    </div>
  );
}

function ReservationRow({ r, type }) {
  const n = Math.ceil((new Date(r.checkOutDate) - new Date(r.checkInDate)) / 86400000);
  const guestsLabel = r.guests?.adults
    ? `${r.guests.adults} adulte${r.guests.adults > 1 ? "s" : ""}${r.guests.children > 0 ? ` · ${r.guests.children} enfant${r.guests.children > 1 ? "s" : ""}` : ""}`
    : null;

  const icons = {
    arrival:   <TbDoorEnter className="text-emerald-500" size={14} />,
    departure: <TbDoorExit  className="text-blue-500"    size={14} />,
    ongoing:   <TbMoonStars className="text-violet-500"  size={14} />,
  };
  const typeColors = {
    arrival:   "text-emerald-500",
    departure: "text-blue-500",
    ongoing:   "text-violet-500",
  };
  const typeLabels = {
    arrival:   "Arrivée",
    departure: "Départ",
  };

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
        {icons[type]}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {r.guest?.firstName} {r.guest?.lastName}
        </p>
        <p className="text-xs text-gray-400 truncate">
          {r.roomId?.title || "—"} · {n} nuit{n > 1 ? "s" : ""}
          {guestsLabel && ` · ${guestsLabel}`}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-gray-900">{r.totalPrice?.toLocaleString("fr-FR")} €</p>
        {type === "ongoing" ? (
          <p className={`text-xs font-medium ${typeColors[type]}`}>→ {fmtShort(r.checkOutDate)}</p>
        ) : (
          <p className={`text-xs font-medium ${typeColors[type]}`}>{typeLabels[type]}</p>
        )}
      </div>
    </div>
  );
}

function AlertCard({ icon: Icon, title, desc, color, onClick }) {
  const colors = {
    orange: { border: "border-orange-200", bg: "bg-orange-50", icon: "text-orange-500", title: "text-orange-800", desc: "text-orange-600" },
    blue:   { border: "border-blue-200",   bg: "bg-blue-50",   icon: "text-blue-500",   title: "text-blue-800",   desc: "text-blue-600"   },
  };
  const c = colors[color] || colors.orange;
  return (
    <div
      onClick={onClick}
      className={`flex items-center gap-3 p-3.5 rounded-xl border ${c.border} ${c.bg} cursor-pointer hover:opacity-90 transition`}
    >
      <Icon className={`${c.icon} shrink-0`} size={18} />
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold ${c.title}`}>{title}</p>
        <p className={`text-xs ${c.desc} truncate`}>{desc}</p>
      </div>
      <TbChevronRight className={`${c.icon} shrink-0`} size={14} />
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────
export default function Dashboard({ setActivePage }) {
  const { hotelId } = useParams();
  const [time, setTime]   = useState(fmtTime());
  const [stats, setStats] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { data: hotel } = useFetch(`https://api.komorotel.com/api/hotels/find/${hotelId}`);
  const { data: reservations = [], refreshData } = useFetch(
    `https://api.komorotel.com/api/reservation/hotel/${hotelId}`
  );

  useEffect(() => {
    const m = new Date().getMonth();
    const y = new Date().getFullYear();
    axios.get(
      `https://api.komorotel.com/api/reservation/hotel/${hotelId}/stats?month=${m}&year=${y}`,
      { withCredentials: true }
    ).then(r => setStats(r.data)).catch(console.error);
  }, [hotelId, lastRefresh]);

  useEffect(() => {
    const t = setInterval(() => setTime(fmtTime()), 30000);
    return () => clearInterval(t);
  }, []);

  const handleRefresh = () => { refreshData(); setLastRefresh(new Date()); };

  // ── Calculs opérationnels ──────────────────────────────────────
  const ops = useMemo(() => {
    const t = today();

    const arrivals = reservations.filter(r =>
      r.status === "confirmed" &&
      normalize(r.checkInDate).getTime() === t.getTime()
    );
    const departures = reservations.filter(r =>
      ["confirmed", "completed"].includes(r.status) &&
      normalize(r.checkOutDate).getTime() === t.getTime()
    );
    const ongoing = reservations.filter(r =>
      r.status === "confirmed" &&
      normalize(r.checkInDate) < t &&
      normalize(r.checkOutDate) > t
    );
    const pendingCancel = reservations.filter(r => r.status === "pending_cancel");

    const upcoming7 = reservations.filter(r => {
      const ci = normalize(r.checkInDate);
      const in7 = new Date(t); in7.setDate(in7.getDate() + 7);
      return r.status === "confirmed" && ci > t && ci <= in7;
    });

    // Outlook 7 jours — arrivées par jour
    const outlook = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(t); d.setDate(d.getDate() + i + 1);
      const dn = normalize(d);
      const count = reservations.filter(r =>
        r.status === "confirmed" &&
        normalize(r.checkInDate).getTime() === dn.getTime()
      ).length;
      return {
        date: d,
        count,
        label: d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric" })
      };
    });

    // Source mix
    const siteCount  = reservations.filter(r => r.source === "site"  && ["confirmed","completed"].includes(r.status)).length;
    const adminCount = reservations.filter(r => r.source === "admin" && ["confirmed","completed"].includes(r.status)).length;
    const totalActive = siteCount + adminCount;
    const sitePct  = totalActive > 0 ? Math.round((siteCount  / totalActive) * 100) : 0;
    const adminPct = totalActive > 0 ? Math.round((adminCount / totalActive) * 100) : 0;

    return { arrivals, departures, ongoing, pendingCancel, upcoming7, outlook, siteCount, adminCount, sitePct, adminPct };
  }, [reservations]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const GreetIcon = hour < 12 ? TbSunrise : hour < 18 ? TbHome : TbMoonStars;
  const maxOutlook = Math.max(...ops.outlook.map(o => o.count), 1);

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] p-6 flex flex-col gap-6">

      {/* ── HEADER ──────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GreetIcon className="text-[#3A9D23]" size={18} />
            <p className="text-sm text-gray-500 font-medium capitalize">{greeting} — {fmtDate(new Date())}</p>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {hotel?.name || "Dashboard"}
          </h1>
          {hotel?.city && (
            <p className="text-sm text-gray-400 mt-0.5">{hotel.city}{hotel.island ? `, ${hotel.island}` : ""}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-white border border-gray-100 rounded-2xl px-4 py-2.5 shadow-sm text-center">
            <p className="text-xs text-gray-400">Heure locale</p>
            <p className="text-lg font-bold text-gray-900 font-mono">{time}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="w-10 h-10 bg-white border border-gray-100 rounded-xl flex items-center justify-center shadow-sm hover:bg-gray-50 transition text-gray-400 hover:text-gray-600"
          >
            <TbRefresh size={18} />
          </button>
        </div>
      </div>

      {/* ── ACCÈS RAPIDES — horizontal sous le header ───────────── */}
      <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Accès rapides</p>
      <div className="flex gap-3">
        {[
          { label: "Nouvelle réservation", sub: "Saisie manuelle",      icon: TbCalendarCheck, page: "book",         color: "text-[#3A9D23]", bg: "bg-green-50"  },
          { label: "Calendrier",           sub: "Prix & disponibilités", icon: TbBed,           page: "inventory",    color: "text-blue-600",  bg: "bg-blue-50"   },
          { label: "Réservations",         sub: "Toutes les résa",      icon: TbUsers,         page: "reservations", color: "text-violet-600",bg: "bg-violet-50" },
          { label: "Avis clients",         sub: "Notes & commentaires", icon: TbStar,          page: "reviews",      color: "text-amber-600", bg: "bg-amber-50"  },
        ].map((item, i) => {
          const Icon = item.icon;
          return (
            <button
              key={i}
              onClick={() => setActivePage?.(item.page)}
              className="flex-1 flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left group"
            >
              <div className={`w-9 h-9 rounded-xl ${item.bg} flex items-center justify-center shrink-0`}>
                <Icon className={item.color} size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-900 truncate">{item.label}</p>
                <p className="text-xs text-gray-400 truncate">{item.sub}</p>
              </div>
              <TbChevronRight className="text-gray-300 group-hover:text-gray-500 transition shrink-0" size={13} />
            </button>
          );
        })}
      </div>
      </div>

      {/* ── ALERTES ─────────────────────────────────────────────── */}
      {(ops.pendingCancel.length > 0 || ops.arrivals.length > 0) && (
        <div className="flex flex-col gap-2">
          {ops.pendingCancel.length > 0 && (
            <AlertCard
              icon={TbAlertTriangle}
              title={`${ops.pendingCancel.length} demande${ops.pendingCancel.length > 1 ? "s" : ""} d'annulation en attente`}
              desc="Action requise — cliquez pour traiter"
              color="orange"
              onClick={() => setActivePage?.("reservations")}
            />
          )}
          {ops.arrivals.length > 0 && (
            <AlertCard
              icon={TbDoorEnter}
              title={`${ops.arrivals.length} arrivée${ops.arrivals.length > 1 ? "s" : ""} prévue${ops.arrivals.length > 1 ? "s" : ""} aujourd'hui`}
              desc={ops.arrivals.map(r => `${r.guest?.firstName} ${r.guest?.lastName}`).join(", ")}
              color="blue"
              onClick={() => setActivePage?.("reservations")}
            />
          )}
        </div>
      )}

      {/* ── KPI CARDS ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={TbDoorEnter}
          label="Arrivées aujourd'hui"
          value={ops.arrivals.length}
          sub={ops.departures.length > 0
            ? `${ops.departures.length} départ${ops.departures.length > 1 ? "s" : ""}`
            : "Aucun départ"
          }
          color="green"
        />
        <KpiCard
          icon={TbMoonStars}
          label="Séjours en cours"
          value={ops.ongoing.length}
          sub={`${ops.upcoming7.length} à venir cette semaine`}
          color="purple"
        />
        <KpiCard
          icon={TbPercentage}
          label="Taux de remplissage"
          value={stats ? `${stats.occupancyRate}%` : "…"}
          sub={stats
            ? `ADR : ${stats.adr.toLocaleString("fr-FR")} € · ${toKMF(stats.adr)} KMF`
            : "Calcul en cours"
          }
          color="blue"
        />
        <KpiCard
          icon={TbCurrencyEuro}
          label="Revenus du mois"
          value={stats ? `${stats.monthRevenuBrut.toLocaleString("fr-FR")} €` : "…"}
          sub={stats ? `${toKMF(stats.monthRevenuBrut)} KMF` : ""}
          subColor="text-gray-400"
          color="green"
          badge={
            stats?.monthCommissionDue > 0 ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-500 font-semibold">
                Commission : {stats.monthCommissionDue.toLocaleString("fr-FR")} €
              </span>
            ) : null
          }
        />
      </div>

      {/* ── GRILLE PRINCIPALE ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* ── Colonne gauche ── */}
        <div className="lg:col-span-2 flex flex-col gap-4">

          {/* Arrivées + Départs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-emerald-50 rounded-lg flex items-center justify-center">
                    <TbDoorEnter className="text-emerald-600" size={14} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">Arrivées</p>
                </div>
                <Pill color="green">{ops.arrivals.length}</Pill>
              </div>
              {ops.arrivals.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Aucune arrivée aujourd'hui</p>
              ) : (
                ops.arrivals.map(r => <ReservationRow key={r._id} r={r} type="arrival" />)
              )}
            </div>

            <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
                    <TbDoorExit className="text-blue-600" size={14} />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">Départs</p>
                </div>
                <Pill color="blue">{ops.departures.length}</Pill>
              </div>
              {ops.departures.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Aucun départ aujourd'hui</p>
              ) : (
                ops.departures.map(r => <ReservationRow key={r._id} r={r} type="departure" />)
              )}
            </div>
          </div>

          {/* Séjours en cours */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center">
                  <TbMoonStars className="text-violet-600" size={14} />
                </div>
                <p className="text-sm font-semibold text-gray-900">Séjours en cours</p>
              </div>
              <Pill color="purple">{ops.ongoing.length}</Pill>
            </div>
            {ops.ongoing.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">Aucun séjour en cours</p>
            ) : (
              ops.ongoing.map(r => <ReservationRow key={r._id} r={r} type="ongoing" />)
            )}
          </div>

          {/* Outlook 7 jours — barres par jour */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 bg-[#3A9D23]/10 rounded-lg flex items-center justify-center">
                <TbActivity className="text-[#3A9D23]" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Arrivées — 7 prochains jours</p>
            </div>
            <div className="flex items-end gap-2">
              {ops.outlook.map((o, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full bg-gray-100 rounded-full overflow-hidden relative" style={{ height: 32 }}>
                    <div
                      className="bg-[#3A9D23]/70 rounded-full w-full absolute bottom-0 transition-all duration-300"
                      style={{ height: maxOutlook > 0 ? `${Math.round((o.count / maxOutlook) * 100)}%` : "0%" }}
                    />
                  </div>
                  {o.count > 0 && (
                    <p className="text-[10px] font-bold text-[#3A9D23]">{o.count}</p>
                  )}
                  <p className="text-[9px] text-gray-400 text-center leading-tight">{o.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Finances */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                <TbCurrencyEuro className="text-[#3A9D23]" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Finances globales</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              {[
                { label: "Revenus bruts totaux", eur: stats?.revenuBrut,    color: "text-gray-900"   },
                { label: "Commission due (8%)",  eur: stats?.commissionDue, color: "text-orange-500" },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">{item.label}</p>
                  <p className={`text-lg font-bold ${item.color}`}>
                    {item.eur != null ? `${item.eur.toLocaleString("fr-FR")} €` : "…"}
                  </p>
                  {item.eur != null && (
                    <p className="text-xs text-gray-400 mt-0.5">{toKMF(item.eur)} KMF</p>
                  )}
                </div>
              ))}
            </div>

            {stats?.revpar > 0 && (
              <div className="bg-gray-50 rounded-xl p-3 mb-3">
                <p className="text-xs text-gray-400 mb-1">RevPAR (revenu par chambre disponible)</p>
                <p className="text-lg font-bold text-gray-900">{stats.revpar.toLocaleString("fr-FR")} €</p>
                <p className="text-xs text-gray-400 mt-0.5">{toKMF(stats.revpar)} KMF</p>
              </div>
            )}

            {/* Source mix */}
            <div className="pt-3 border-t border-gray-50">
              <p className="text-xs text-gray-400 mb-2">Origine des réservations</p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
                    <div className="bg-blue-400 transition-all" style={{ width: `${ops.sitePct}%` }} />
                    <div className="bg-purple-400 transition-all" style={{ width: `${ops.adminPct}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <TbWorld size={11} className="text-blue-400" />
                    Site {ops.sitePct}%
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <TbBuildingStore size={11} className="text-purple-400" />
                    Admin {ops.adminPct}%
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* ── Colonne droite ── */}
        <div className="flex flex-col gap-4">

          {/* Compteurs globaux */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 bg-gray-50 rounded-lg flex items-center justify-center">
                <TbUsers className="text-gray-500" size={14} />
              </div>
              <p className="text-sm font-semibold text-gray-900">Vue globale</p>
            </div>
            <div className="flex flex-col gap-0">
              {[
                { label: "Confirmées en cours",    value: stats?.confirmed      ?? "…", dot: "bg-emerald-400" },
                { label: "Terminées",               value: stats?.completed      ?? "…", dot: "bg-slate-300"   },
                { label: "Annulations en attente",  value: stats?.pending_cancel ?? "…", dot: "bg-orange-400"  },
                { label: "No-show",                 value: stats?.no_show        ?? "…", dot: "bg-slate-400"   },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2.5 border-b border-gray-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${item.dot}`} />
                    <p className="text-xs text-gray-600">{item.label}</p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}